import React, { useState } from 'react';
import { useApp } from '@/context/AppContext';
import { TopBar } from '@/components/TopBar';
import { Page } from '@/components/Page';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Globe, ShoppingBag, Instagram, MessageCircle, CreditCard, Database,
  ExternalLink, Settings2, PlugZap, ShieldCheck, RefreshCw, Activity, KeyRound, Copy,
} from 'lucide-react';

function WebsiteKeyWizard({ onClose }) {
  const [label, setLabel] = useState('production');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);   // {raw_key, prefix, scopes,...}
  const create = async () => {
    setBusy(true);
    try {
      const r = await api.post('/admin/integration/website-key', { label, name });
      setCreated(r.data.data);
      toast.success('Website key created');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create key'); } finally { setBusy(false); }
  };
  const copy = () => { navigator.clipboard?.writeText(created.raw_key); toast.success('Copied'); };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="website-key-wizard">
        <DialogHeader>
          <DialogTitle>Create Website API Key</DialogTitle>
          <DialogDescription className="flex items-center gap-1.5 text-xs"><ShieldCheck className="h-3.5 w-3.5" /> Least-privilege: products:read + stock:read only. The secret is shown once.</DialogDescription>
        </DialogHeader>
        {!created ? (
          <div className="space-y-3">
            <div><Label className="text-xs">Environment label</Label>
              <Select value={label} onValueChange={setLabel}>
                <SelectTrigger className="mt-1" data-testid="wk-label"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="production">production</SelectItem><SelectItem value="staging">staging</SelectItem></SelectContent>
              </Select></div>
            <div><Label className="text-xs">Name (optional)</Label>
              <Input className="mt-1" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Shabz website" data-testid="wk-name" /></div>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Copy this secret now — it will not be shown again.</p>
            <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/30 p-2">
              <code className="flex-1 break-all text-xs" data-testid="wk-secret">{created.raw_key}</code>
              <button onClick={copy} data-testid="wk-copy" className="rounded-md p-1.5 hover:bg-white/5"><Copy className="h-4 w-4" /></button>
            </div>
            <p className="text-[11px] text-muted-foreground">Scopes: {(created.scopes || []).join(', ')} · Label: {created.label}</p>
          </div>
        )}
        <DialogFooter>
          {!created
            ? (<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={create} disabled={busy} data-testid="wk-create">{busy ? 'Creating…' : 'Create key'}</Button></>)
            : (<Button onClick={onClose} data-testid="wk-done">Done</Button>)}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const HealthCell = ({ label, value }) => (
  <div className="rounded-lg border border-border px-2.5 py-1.5">
    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="font-mono text-sm font-semibold">{value ?? '—'}</p>
  </div>
);

const META = {
  website: { icon: Globe, blurb: 'Your official store website link.' },
  online_orders: { icon: ShoppingBag, blurb: 'Orders coming from your website (future).' },
  instagram: { icon: Instagram, blurb: 'DMs, comment leads & enquiries (future).' },
  whatsapp: { icon: MessageCircle, blurb: 'Customer chat, order & payment updates (future).' },
  payments: { icon: CreditCard, blurb: 'Online payment gateway (future).' },
  billing: { icon: Database, blurb: 'Your live billing database — the source of truth.' },
};

const StatusBadge = ({ status }) => {
  const s = (status || 'not_connected').toLowerCase();
  const map = {
    connected: 'bg-primary/15 text-primary ring-1 ring-primary/30',
    not_connected: 'bg-muted/40 text-muted-foreground ring-1 ring-border',
    error: 'bg-destructive/15 text-destructive ring-1 ring-destructive/30',
  };
  const label = { connected: 'Connected', not_connected: 'Not Connected', error: 'Error' }[s] || 'Not Connected';
  return <span data-testid={`badge-${s}`} className={`inline-block rounded-md px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${map[s] || map.not_connected}`}>{label}</span>;
};

// Configure fields per integration type (secrets rendered as password + never echoed back).
const FIELDS = {
  website: [{ k: 'website_url', label: 'Website URL', ph: 'https://your-store.com' }],
  online_orders: [{ k: 'source', label: 'Order source name', ph: 'e.g. Website' }, { k: 'base_url', label: 'Store base URL', ph: 'https://…' }],
  instagram: [{ k: 'handle', label: 'Instagram handle', ph: '@shabzcloset' }, { k: 'access_token', label: 'Access token', ph: '••••••', secret: true }],
  whatsapp: [{ k: 'phone_number', label: 'WhatsApp business number', ph: '+91…' }, { k: 'access_token', label: 'Access token', ph: '••••••', secret: true }],
  payments: [{ k: 'gateway', label: 'Gateway', ph: 'e.g. Razorpay' }, { k: 'merchant_id', label: 'Merchant / Account ID', ph: '' }, { k: 'mode', label: 'Mode', select: ['test', 'live'] }, { k: 'api_secret', label: 'API secret', ph: '••••••', secret: true }, { k: 'webhook_secret', label: 'Webhook secret', ph: '••••••', secret: true }],
  billing: [],
};

function ConfigureDialog({ integration, onClose, onSaved }) {
  const t = integration.type;
  const fields = FIELDS[t] || [];
  const [form, setForm] = useState(() => {
    const f = {};
    fields.forEach((fl) => { f[fl.k] = fl.secret ? '' : (integration.configuration?.[fl.k] ?? (fl.select ? fl.select[0] : '')); });
    return f;
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      // Only send secret fields if the owner actually typed something.
      const configuration = {};
      fields.forEach((fl) => { if (!fl.secret || (form[fl.k] && form[fl.k].length)) configuration[fl.k] = form[fl.k]; });
      await api.post(`/integrations/${t}/configure`, { configuration });
      toast.success('Saved'); onSaved && onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="integration-configure-dialog">
        <DialogHeader>
          <DialogTitle>Configure {integration.display_name}</DialogTitle>
          <DialogDescription className="flex items-center gap-1.5 text-xs"><ShieldCheck className="h-3.5 w-3.5" /> Secrets are stored securely on the server and never shown again.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {fields.length === 0 && <p className="text-sm text-muted-foreground">No settings required for this connection.</p>}
          {fields.map((fl) => (
            <div key={fl.k}>
              <Label className="text-xs">{fl.label}{fl.secret ? ' (write-only)' : ''}</Label>
              {fl.select ? (
                <Select value={form[fl.k]} onValueChange={(v) => setForm({ ...form, [fl.k]: v })}>
                  <SelectTrigger className="mt-1" data-testid={`cfg-${fl.k}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{fl.select.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}</SelectContent>
                </Select>
              ) : (
                <Input className="mt-1" data-testid={`cfg-${fl.k}`} type={fl.secret ? 'password' : 'text'}
                  autoComplete={fl.secret ? 'new-password' : 'off'} placeholder={fl.ph}
                  value={form[fl.k]} onChange={(e) => setForm({ ...form, [fl.k]: e.target.value })} />
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="cfg-save">{busy ? 'Saving…' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IntegrationCard({ it, onConfigure, onChanged }) {
  const Icon = META[it.type]?.icon || PlugZap;
  const isConnected = it.status === 'connected';
  const call = async (action) => {
    try {
      const r = await api.post(`/integrations/${it.type}/${action}`);
      if (action === 'test') {
        const res = r.data.data;
        res.ok ? toast.success(res.message) : toast.error(res.message);
      } else { toast.success(action === 'enable' ? 'Enabled' : 'Disabled'); onChanged && onChanged(); }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
  };
  const openWebsite = () => {
    const url = it.configuration?.website_url;
    if (url) window.open(url, '_blank', 'noopener');
    else toast.message('Website not configured', { description: 'Add your URL under Configure.' });
  };
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid={`integration-card-${it.type}`}>
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"><Icon className="h-5 w-5" /></span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-display text-base font-semibold">{it.display_name}</h3>
            <StatusBadge status={it.status} />
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">{META[it.type]?.blurb}</p>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-muted-foreground">
            <span>Last sync: {it.last_sync_at ? new Date(it.last_sync_at).toLocaleString() : '—'}</span>
            <span>Enabled: {it.enabled ? 'Yes' : 'No'}</span>
          </div>
          {it.last_error_message && <p className="mt-1 text-[11px] text-destructive">{it.last_error_message}</p>}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {it.type === 'website' && (
          <Button size="sm" variant="secondary" className="h-9" onClick={openWebsite} data-testid={`open-website`}>
            <ExternalLink className="mr-1.5 h-4 w-4" /> Open Website
          </Button>
        )}
        {it.type === 'online_orders' && (
          <Button size="sm" variant="secondary" className="h-9" data-testid="view-orders" onClick={() => toast.message('No online orders connected yet.')}>
            <ShoppingBag className="mr-1.5 h-4 w-4" /> View Orders
          </Button>
        )}
        {(it.type === 'instagram' || it.type === 'whatsapp') && !isConnected && (
          <Button size="sm" variant="secondary" className="h-9" data-testid={`connect-${it.type}`} onClick={() => onConfigure(it)}>
            <PlugZap className="mr-1.5 h-4 w-4" /> Connect
          </Button>
        )}
        {it.type !== 'billing' && (
          <Button size="sm" variant="outline" className="h-9" data-testid={`configure-${it.type}`} onClick={() => onConfigure(it)}>
            <Settings2 className="mr-1.5 h-4 w-4" /> Configure
          </Button>
        )}
        <Button size="sm" variant="outline" className="h-9" data-testid={`test-${it.type}`} onClick={() => call('test')}>
          <RefreshCw className="mr-1.5 h-4 w-4" /> Test Connection
        </Button>
        {it.type !== 'billing' && (it.enabled
          ? <Button size="sm" variant="ghost" className="h-9 text-muted-foreground" data-testid={`disable-${it.type}`} onClick={() => call('disable')}>Disable</Button>
          : <Button size="sm" variant="ghost" className="h-9 text-primary" data-testid={`enable-${it.type}`} onClick={() => call('enable')}>Enable</Button>)}
      </div>
    </div>
  );
}

export default function Integrations() {
  const { user } = useApp();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [configuring, setConfiguring] = useState(null);
  const [keyWizard, setKeyWizard] = useState(false);
  const isOwner = user?.role === 'OWNER' || user?.owner_access;

  const { data: items = [], isLoading, isError } = useQuery({
    queryKey: ['integrations'],
    queryFn: async () => (await api.get('/integrations')).data.data,
    enabled: isOwner,
  });
  const { data: health } = useQuery({
    queryKey: ['system-health'],
    queryFn: async () => (await api.get('/system/health')).data.data,
    enabled: isOwner, refetchInterval: 60000,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['integrations'] });

  if (!isOwner) {
    return (
      <Page>
        <TopBar title="Integrations" showStore={false} />
        <div className="p-6 text-center text-sm text-muted-foreground" data-testid="integrations-forbidden">
          Integration settings are available to the Owner only.
        </div>
      </Page>
    );
  }

  return (
    <Page>
      <TopBar title="Integrations" showStore={false} />
      <div className="space-y-4 p-4" data-testid="integrations-page">
        <div className="rounded-xl border border-primary/30 bg-primary/5 p-3 text-xs text-muted-foreground">
          Connect SHABZ CLOSET to your website, online orders, Instagram, WhatsApp and payments. Everything here is
          <span className="font-semibold text-foreground"> inactive until you configure and enable it</span> — your billing data is never affected.
        </div>

        {health && (
          <div className="rounded-2xl border border-border bg-card p-4" data-testid="system-health">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="flex items-center gap-1.5 font-display text-base font-semibold"><Activity className="h-4 w-4 text-primary" /> System Health</h3>
              <span className={`rounded-md px-2 py-0.5 text-[11px] font-bold uppercase ${health.overall === 'ok' ? 'bg-primary/15 text-primary' : 'bg-destructive/15 text-destructive'}`}>{health.overall}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-3">
              <HealthCell label="Database" value={health.components?.database?.status} />
              <HealthCell label="Billing" value={`${health.components?.billing?.sales} sales`} />
              <HealthCell label="Read API" value={`${health.components?.read_api?.active_keys} keys`} />
              <HealthCell label="Webhooks" value={health.components?.webhooks?.status} />
              <HealthCell label="Pending events" value={health.components?.webhooks?.pending} />
              <HealthCell label="Version" value={`v${health.versions?.app}`} />
            </div>
          </div>
        )}
        {isError ? (
          <p className="py-10 text-center text-sm text-destructive">Could not load integrations.</p>
        ) : isLoading ? (
          <p className="py-10 text-center text-sm text-muted-foreground">Loading…</p>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <h3 className="font-display text-base font-semibold">Connected systems</h3>
              <Button size="sm" className="h-9" data-testid="website-key-wizard-open" onClick={() => setKeyWizard(true)}>
                <KeyRound className="mr-1.5 h-4 w-4" /> Create Website API Key
              </Button>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {items.map((it) => (
                <IntegrationCard key={it.type} it={it} onConfigure={setConfiguring} onChanged={refresh} />
              ))}
            </div>
          </>
        )}
      </div>
      {configuring && <ConfigureDialog integration={configuring} onClose={() => setConfiguring(null)} onSaved={refresh} />}
      {keyWizard && <WebsiteKeyWizard onClose={() => setKeyWizard(false)} />}
    </Page>
  );
}
